import moment from "moment";

export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

// Weekday-only pages (Booker, Harcast) default to the current week on a weekday visit - but on a
// weekend visit (Saturday/Sunday) that week's Mon-Fri has already gone by, so defaulting to it
// would show a stale, already-finished week. Weekend visits jump straight to the upcoming week's
// Monday instead. Used both for each page's initial load and for its "Today" button.
export function getDefaultSelectedWeek() {
  const now = moment();
  const isWeekend = now.day() === 0 || now.day() === 6; // Sunday = 0, Saturday = 6
  return isWeekend ? now.clone().add(1, "week").startOf("isoWeek").toDate() : now.toDate();
}

// New bookings (Desk, Parking, Meeting Room, Office) default to today's date (rolling weekends
// forward to Monday) when the Booker calendar is showing the current real-world week - but if the
// user has already navigated forward to a future week before opening "New Booking", defaulting to
// today would silently create a booking on a day that isn't even the one being looked at. In that
// case, default to the Monday of the future week being viewed instead. "Future" is judged by
// isoWeek so it only kicks in once the user has moved to a genuinely different week, not just a
// different day within the same week - viewing the current week still defaults to today exactly
// as before.
export function getDefaultBookingDay(selectedWeek) {
  const now = moment();
  const isFutureWeek = selectedWeek && moment(selectedWeek).isAfter(now, "isoWeek");
  if (isFutureWeek) {
    return moment(selectedWeek).startOf("isoWeek");
  }
  return now.day() === 0 ? now.clone().add(1, "day") : now.day() === 6 ? now.clone().add(2, "day") : now.clone();
}

// Away/Off entries come from an external calendar (Edit Company > Booker) and only carry a
// display name (no employee UID), so matching against a portal user has to compare on name text
// rather than by id like everything else in the app. Two names are treated as the same person if
// they're identical, OR if one is a whole-word prefix of the other (e.g. portal "Ash" auto-matches
// e-days "Ash Richards") - this covers the common case of a portal display name being a shortened
// version of the fuller name e-days uses, with no manual setup needed. It's checked on whole word
// boundaries (not a raw substring match) so a short name like "Ash" won't wrongly match an
// unrelated "Ashley". The optional "Away/Off Calendar Name" override (the "away_name" custom
// claim, set per-employee on the Employees page) only needs to be used for the cases this
// heuristic can't resolve on its own, e.g. a nickname that isn't a prefix of the full name at all
// (portal "Ash" vs e-days "Ashley Richards").
export function namesLikelyMatch(nameA, nameB) {
  const a = (nameA || "").trim().toLowerCase();
  const b = (nameB || "").trim().toLowerCase();
  if (!a || !b) {
    return false;
  }
  return a === b || `${b} `.startsWith(`${a} `) || `${a} `.startsWith(`${b} `);
}